import { getIconByKeyword } from "@/components/AccommodationModalDetailed/getIconByKeyword.ts";
import { useGetExtrasTranslated } from "@/components/ReservationOptions/hooks/useGetExtrasTranslated.ts";
import { formatCurrency } from "@/helpers/formatCurrency.ts";
import { useGetRGTranslated } from "@/helpers/translation/useGetRGTranslated.ts";
import { useGetRPTranslated } from "@/helpers/translation/useGetRPTranslated.ts";
import { MandatoryExtra, ParcelObjectType, RateGroup } from "@/helpers/types/types.ts";
import { useEngineStore } from "@/stores/EngineStore/EngineStore.ts";
import { CartAccommodationItem } from "@/stores/EngineStore/interfaces/CartAccommodationItem.ts";
import format from "date-fns/format";
import { Baby, Bed, Calendar, Caravan, Tent, Users } from "lucide-react";
import { MouseEvent, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useShallow } from "zustand/react/shallow";

const ADULT_KEYWORDS = /\badult\b|\bodrasl/i;
const CHILD_KEYWORDS = /\bchild\b|\bkid\b|\bdjeca\b|\bkind\b|\benfant\b|\bbaby\b|\bbeb/i;

export const AccommodationOverview = (props: {
  cartAccommodation: CartAccommodationItem;
  handleOpenGallery: (rg: RateGroup) => void;
}) => {
  const { cartAccommodation } = props;
  const { calculatedCartData } = useEngineStore(
    useShallow((state) => ({
      calculatedCartData: state.calculatedCartData?.accommodations.find(
        (acc) => acc.identifier === cartAccommodation.id,
      ),
    })),
  );

  const { t } = useTranslation();
  const { getTranslateRPData } = useGetRPTranslated();
  const { getTranslateRGData } = useGetRGTranslated();
  const getExtrasTranslated = useGetExtrasTranslated();

  const { name: RGName } = useMemo(
    () => getTranslateRGData(cartAccommodation.rateGroup),
    [getTranslateRGData, cartAccommodation.rateGroup],
  );
  const { name: RPName } = useMemo(
    () => getTranslateRPData(cartAccommodation.ratePlan),
    [getTranslateRPData, cartAccommodation.ratePlan],
  );

  const renderVehicleTypeIcon = () => {
    switch (cartAccommodation.parcelObjectType) {
      case ParcelObjectType.Camper:
      case ParcelObjectType.Caravan:
        return <Caravan size={16} />;
      case ParcelObjectType.Tent:
        return <Tent size={16} />;

      default:
        return <Tent size={16} />;
    }
  };

  const handleAccommodationInfoClick = (e: MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    e.preventDefault();
    props.handleOpenGallery(cartAccommodation.rateGroup);
  };

  const image =
    cartAccommodation.rateGroup.images?.find((img) => img.isPrimary)?.url ??
    cartAccommodation.rateGroup.images?.[0]?.url;

  const getExtraTotal = (extra: MandatoryExtra) => {
    return calculatedCartData?.extras?.find((e) => e.id === extra.id)?.total ?? 0;
  };

  const { adultExtras, childExtras, otherExtras } = useMemo(() => {
    const adult: MandatoryExtra[] = [];
    const child: MandatoryExtra[] = [];
    const other: MandatoryExtra[] = [];

    for (const extra of cartAccommodation.mandatoryExtras) {
      const { name } = getExtrasTranslated(extra);
      if (ADULT_KEYWORDS.test(name)) {
        adult.push(extra);
      } else if (CHILD_KEYWORDS.test(name)) {
        child.push(extra);
      } else {
        other.push(extra);
      }
    }

    return { adultExtras: adult, childExtras: child, otherExtras: other };
  }, [cartAccommodation.mandatoryExtras, getExtrasTranslated]);

  const adultExtrasTotal = adultExtras.reduce((sum, e) => sum + getExtraTotal(e), 0);
  const childExtrasTotal = childExtras.reduce((sum, e) => sum + getExtraTotal(e), 0);

  return (
    <div className="accommodation-overview">
      {image && (
        <div className="accommodation-overview__image" onClick={handleAccommodationInfoClick}>
          <img src={image} alt={RGName} />
        </div>
      )}
      <div>
        <div className="accommodation-overview__title--container">
          <h6 className="accommodation-overview__title">{RGName}</h6>
        </div>
        <p className="accommodation-overview__row">
          <Calendar size={16} />
          {format(new Date(cartAccommodation.dateRange[0]!), "dd.MM.yyyy")} -{" "}
          {format(new Date(cartAccommodation.dateRange[1]!), "dd.MM.yyyy")}
        </p>
        <p className="accommodation-overview__row accommodation-overview__row--has-price">
          <Bed size={16} />
          {RPName}
          <span className="accommodation-overview__row-price">
            {formatCurrency(calculatedCartData?.rateBasePrice || 0)}
          </span>
        </p>
        <div
          className={`accommodation-overview__row ${
            adultExtrasTotal > 0 ? "accommodation-overview__row--has-price" : ""
          }`}
        >
          <Users size={16} />
          <span>
            {t("adultsCount")}: {cartAccommodation.adults.length}
          </span>
          {adultExtrasTotal > 0 && (
            <span className="accommodation-overview__row-price">{formatCurrency(adultExtrasTotal)}</span>
          )}
        </div>
        {cartAccommodation.children.length > 0 && (
          <div
            className={`accommodation-overview__row ${
              childExtrasTotal > 0 ? "accommodation-overview__row--has-price" : ""
            }`}
          >
            <Baby size={16} />
            <span>
              {t("childrenCount")}: {cartAccommodation.children.length}
            </span>
            {childExtrasTotal > 0 && (
              <span className="accommodation-overview__row-price">{formatCurrency(childExtrasTotal)}</span>
            )}
          </div>
        )}
        {cartAccommodation.parcelObjectType && (
          <div className="accommodation-overview__row accommodation-overview__row--top-aligned">
            {renderVehicleTypeIcon()}
            <div className="accommodation-overview__row-stacked">
              <span>{t(`campSpecific.${cartAccommodation.parcelObjectType.toLowerCase()}`)}</span>
              {cartAccommodation.parcelObjectDimensions && (
                <span className="accommodation-overview__row-sub">
                  {t("campSpecific.size")}: {cartAccommodation.parcelObjectDimensions}
                </span>
              )}
              {cartAccommodation.parcelObjectAdditionalTentDimensions && (
                <span className="accommodation-overview__row-sub">
                  {t("campSpecific.additionalTentSize")}:{" "}
                  {cartAccommodation.parcelObjectAdditionalTentDimensions}
                </span>
              )}
            </div>
          </div>
        )}
        {otherExtras.map((extra) => {
          const { name } = getExtrasTranslated(extra);
          const Icon = getIconByKeyword(name);
          return (
            <div
              key={extra.id}
              className="accommodation-overview__row accommodation-overview__row--has-price"
            >
              <Icon size={16} />
              <span>{name}</span>
              <span className="accommodation-overview__row-price">
                {formatCurrency(getExtraTotal(extra))}
              </span>
            </div>
          );
        })}
        {cartAccommodation.optionalExtras.map((extra) => {
          const calcExtra = calculatedCartData?.extras?.find((e) => e.id === extra.id);
          const { name } = getExtrasTranslated(extra);
          const Icon = getIconByKeyword(name);
          return (
            <div
              key={extra.id}
              className="accommodation-overview__row accommodation-overview__row--has-price"
            >
              <Icon size={16} />
              <span>{name}</span>
              <span className="accommodation-overview__row-price">
                {formatCurrency(calcExtra?.total || extra.price * extra.value)}
              </span>
            </div>
          );
        })}
        <div className="accommodation-overview__row accommodation-overview__row--total">
          <span>{t("total")}</span>
          <span className="accommodation-overview__row-price">
            {formatCurrency(calculatedCartData?.priceBreakdown?.withDiscount || 0)}
          </span>
        </div>
      </div>
    </div>
  );
};
